import React from 'react';

export type CarrierTabId =
  | 'overview'
  | 'inspections'
  | 'crashes'
  | 'violations'
  | 'vins'
  | 'basics'
  | 'predictions'
  | 'analytics'
  | 'network'
  | 'status-history'
  | 'fair-assessment';

export interface TabNavigationProps {
  activeTab: CarrierTabId;
  setActiveTab: (tab: CarrierTabId) => void;
  basicsCount: number;
  violationsCount: number;
  inspectionsCount: number;
  crashesCount: number;
  vehiclesCount: number;
  // Extra tab buttons (e.g. Authority & Insurance) rendered in the same
  // flex-wrap row as the buttons below, so they wrap and align with them
  // instead of sitting in a separate box underneath.
  children?: React.ReactNode;
}

const TabNavigation: React.FC<TabNavigationProps> = ({
  activeTab,
  setActiveTab,
  basicsCount,
  violationsCount,
  inspectionsCount,
  crashesCount,
  vehiclesCount,
  children,
}) => {
  const tabs: Array<{ id: CarrierTabId; label: string; count: number | null }> = [
    { id: 'overview', label: 'Overview', count: null },
    { id: 'analytics', label: 'Analytics', count: null },
    { id: 'basics', label: 'BASIC Scores', count: basicsCount },
    { id: 'predictions', label: 'Predictions', count: null },
    { id: 'fair-assessment', label: 'Fair Assessment', count: null },
    { id: 'violations', label: 'Violations', count: violationsCount },
    { id: 'inspections', label: 'Inspections', count: inspectionsCount },
    { id: 'crashes', label: 'Crashes', count: crashesCount },
    { id: 'vins', label: 'VINs', count: vehiclesCount },
    { id: 'status-history', label: 'Status History', count: null },
    { id: 'network', label: 'Network', count: null },
  ];

  return (
    <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap', marginBottom: 'var(--space-lg)', padding: 'var(--space-md)' }}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          style={{
            padding: 'var(--space-sm) var(--space-md)',
            background: activeTab === tab.id ? 'var(--color-primary)' : 'white',
            color: activeTab === tab.id ? 'white' : 'var(--color-gray-700)',
            border: `1px solid ${activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-gray-300)'}`,
            borderRadius: 'var(--radius-md)',
            fontWeight: '500',
            fontSize: '0.875rem',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-xs)'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== tab.id) {
              e.currentTarget.style.background = 'var(--color-gray-50)';
              e.currentTarget.style.borderColor = 'var(--color-gray-400)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== tab.id) {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.borderColor = 'var(--color-gray-300)';
            }
          }}
        >
          <span>{tab.label}</span>
          {tab.count !== null && (
            <span style={{
              padding: '0.125rem 0.375rem',
              fontSize: '0.75rem',
              background: activeTab === tab.id ? 'rgba(255, 255, 255, 0.2)' : 'var(--color-gray-100)',
              color: activeTab === tab.id ? 'white' : 'var(--color-gray-600)',
              borderRadius: 'var(--radius-sm)',
              fontWeight: '500'
            }}>
              {tab.count}
            </span>
          )}
        </button>
      ))}
      {children}
    </div>
  );
};

export default TabNavigation;
